import moment from "moment";
import { isEmpty } from "lodash";

import { updateUpgrade } from "store/upgradeLibrary/actions";
import { updateEcmInputMapUpgrade } from "store/parametricAnalysis/actions";
import { updateCommunityEcmInputMapUpgrade } from "store/community/actions";
import { createChangeBatch } from "store/root/changeBatch";

import getModelTemplates from "utils/getModelTemplates";
import { getMatchCodeRefs, getMatchFloorHeaderCodeRefs } from "utils/enclosure/components";
import { getAllWindowAccessors, getMatchingWindowCodeRefs } from "utils/enclosure/windows";
import { getNominalRValueUDef } from "utils/enclosure/getNominalRValue";
import { idIfyDate } from "utils";
import { prebuiltEcms } from "features/ParametricAnalysis/templates/preBuiltEcms";
import { removeUndefined } from "utils/generalUtils/generalUtils";

const fieldCodeTypeMap = {
    Wall: "wallInsType",
    Window: "windowType",
    Floor: "expFloorInsType",
    FloorsAbove: "floorsAboveInsType",
    CeilingFlat: "ceilingInsType",
    Ceiling: "ceilingInsType",
    FloorHeader: "floorHeaderInsType",
    CrawlspaceWall: "crawlspaceWallInsType", //SPECIAL CASE also "ponyWallInsType"
    BasementWall: "intAddedInsType",
    Lintel: "lintelInsType", //This one is tricky, it can be in walls or basements, but both use lintelInsType
    FloorsAdded: "slabInsType", //NOT TESTED
};

const fieldPathCodeTypeMap = {
    Wall: "wallInsType",
    Window: "windowType",
    Floor: "expFloorInsType",
    FloorsAbove: "floor.floorsAboveInsType",
    CeilingFlat: "ceilingInsType",
    Ceiling: "ceilingInsType",
    FloorHeader: "floorHeaderInsType",
    CrawlspaceWall: "wall.crawlspaceWallInsType", //SPECIAL CASE also "ponyWallInsType"
    BasementWall: "wall.intAddedInsType",
    Lintel: "lintelInsType", //This one is tricky, it can be in walls or basements, but both use lintelInsType
    FloorsAdded: "floor.slabInsType", //NOT TESTED
};

/** Upgrade library: keep Redux and `newForm.upgradeDrawer.codes` in sync (drawer submit reads the form). */
const syncUpgradeLibraryCodesToDrawerForm = (
    dispatch,
    queueChange,
    { currentPackage, selectedUpgrade, codeRef, codePayload }
) => {
    if (!currentPackage || !selectedUpgrade || !codeRef) return;

    const currentUpgrade = currentPackage.upgrades?.[selectedUpgrade] || {};
    const mergedLibraryCodes = {
        ...(currentUpgrade.codes || {}),
        [codeRef]: codePayload,
    };

    dispatch(
        updateUpgrade(selectedUpgrade, {
            ...currentUpgrade,
            codes: mergedLibraryCodes,
        })
    );
    queueChange("newForm.upgradeDrawer.codes", mergedLibraryCodes);
};

export const standardCodeLogic = async ({
    change,
    componentCodeType,
    componentId,
    code: initialCode,
    inputName = "",
    codeType, // "S" or "U" // TODO: Is this always "S"?
    isCodeLibrary = false,
    isEditing = false,
    editingCodeRef = "",
    dispatch,
    getRValue,
    fieldAccessor,
    components,
    modelCodes,
    addToLibrary = false,
    addToCodeLib,
    uid,
    deleteCode,
    updateUpgradeCodes,
    setAsModelDefault = false,
    selectedComponents = [],
    currentPackage = null,
    selectedUpgrade,
    isEcmInputMap = false,
    ecmInputMapUpgradeType = "",
    ecmInputMapUpgradeId = "",
    ecmInputMapUpgrade: initialEcmInputMapUpgrade = {},
    ecmInputMapLocation = "",
}) => {
    const code = removeUndefined(initialCode);
    const ecmInputMapUpgrade = removeUndefined(initialEcmInputMapUpgrade);

    const { codeRef, value, nominalRValue = null, label, isLibCode, ...rest } = code;
    const codeTypeLabel = codeType === "U" ? "UserDefined" : "Standard";
    const multiComponentAllowed =
        ["Wall", "Ceiling", "CeilingFlat", "Floor", "FloorHeader"].includes(componentCodeType) && !isCodeLibrary;
    // ***************************************
    // 1. Create and change code ref
    // If we are updating a library code, either:
    // (isLibCode===TRUE && !isCodeLibrary) --> we're editing a library code outside of the code library
    // OR
    // (isEditing===TRUE) --> we're editing a code (we want the change decoupled from the rest of the model)
    // OR
    // (isEditing===TRUE && isCodeLibrary===TRUE) --> we're editing a code in the code library
    // Then we must create a new codeRef
    // ***************************************
    const newCodeRef = `${componentCodeType}-${codeType}-${moment().format("YYYYMMDDHHmmssSS")}`;
    const forceNewCodeRef = (isLibCode && !isCodeLibrary) || (isEditing && isCodeLibrary) || isEditing;
    const setCodeRef = forceNewCodeRef ? newCodeRef : editingCodeRef || newCodeRef;

    // ***************************************
    // 2. Fetch rValue, if applicable
    // ***************************************

    let rVal = 0.1;
    let nomRVal = 0.1;
    let effRVal = 0.1;
    let warningType = "";

    const rValueComponent = isCodeLibrary ? "codeLib" : componentId;
    const rValueCalcfieldId = isCodeLibrary ? setCodeRef : inputName;

    await dispatch(
        getRValue({
            codeString: value,
            codeType: componentCodeType,
            componentId: rValueComponent,
            fieldId: rValueCalcfieldId,
        })
    ).then(({ rVal: newRVal, effRVal: newEffRVal = newRVal, warningType: warning }) => {
        rVal = Math.max(0.1, newRVal);
        effRVal = Math.max(0.1, newEffRVal);
        nomRVal = Math.max(0.1, newRVal);
        warningType = warning;
    });

    // ***************************************
    // 3. If in model, do model things
    // ***************************************
    if (!isCodeLibrary) {
        // Create a batch to collect all changes and commit them in one Redux update
        const batch = createChangeBatch(dispatch);

        // ***************************************
        // 3.1. Add code to model's codes
        // ***************************************

        const code = {
            ...rest,
            nominalRValue: nomRVal,
            effectiveRValue: effRVal,
            label,
            value,
            codeRef: setCodeRef,
            warningType,
        };
        //Add to upgrade codes if in upgrade library (Redux + upgrade drawer form `codes`)
        if (currentPackage && selectedUpgrade) {
            syncUpgradeLibraryCodesToDrawerForm(dispatch, batch.queue, {
                currentPackage,
                selectedUpgrade,
                codeRef: code.codeRef,
                codePayload: code,
            });
        }

        // upgradeType, upgradeId, { ...upgrade, upgradeSubType: upgradeType }

        if (isEcmInputMap) {
            handleEcmInputMapCodeLogic({
                queueChange: batch.queue,
                code,
                dispatch,
                ecmInputMapUpgradeType,
                ecmInputMapUpgradeId,
                ecmInputMapUpgrade,
                ecmInputMapLocation,
            });
        }

        batch.queue(`model.modelData.codes.${setCodeRef}`, code);

        // ***************************************
        // 3.2. Update the type fields in the curent form
        // ***************************************
        const fieldValue = {
            codeLabel: label,
            codeRef: setCodeRef,
        };

        batch.queue(fieldAccessor, fieldValue);
        batch.queue(`${fieldAccessor}_effRVal`, effRVal || nomRVal);
        batch.queue(`${fieldAccessor}_nomRVal`, nomRVal);

        const compField = fieldPathCodeTypeMap[componentCodeType];
        if (setAsModelDefault) {
            let defaultValue = {};

            if (compField.includes(".")) {
                let first = compField.split(".")[0];
                let second = compField.split(".")[1];
                defaultValue = {
                    [first]: {
                        [second]: fieldValue,
                        [`${second}_nomRVal`]: nomRVal,
                        [`${second}_effRVal`]: effRVal || nomRVal,
                    },
                };
            } else {
                defaultValue = {
                    [compField]: fieldValue,
                    [`${compField}_nomRVal`]: nomRVal,
                    [`${compField}_effRVal`]: effRVal || nomRVal,
                };
            }

            batch.queue(`model.modelData.defaultCodes.${componentCodeType}`, defaultValue);
        }

        if (!isEmpty(selectedComponents) && multiComponentAllowed) {
            selectedComponents.forEach((path) => {
                batch.queue(`model.modelData.components.${path}.${compField}`, fieldValue);
                batch.queue(`model.modelData.components.${path}.${compField}_effRVal`, effRVal || nomRVal);
                batch.queue(`model.modelData.components.${path}.${compField}_nomRVal`, nomRVal);
            });
        }

        //Add warning tag to field
        if (warningType === "compression") {
            batch.queue(`${fieldAccessor}_warning`, "compression");
        } else {
            batch.queue(`${fieldAccessor}_warning`, "");
        }

        // ***************************************
        // 3.3. CONDITIONAL - If editing, update components and upgrades with the same code ref
        // NEW: Nov 22, 2022 - We're changing this logic so that codes are only updated throughout the rest of the model
        // if we're editing a code library code
        // ***************************************
        // if (isEditing && !isCodeLibrary) { //OLD LOGIC WOULD UPDATE ALL CODE IN THE MODEL WHEN EDITING
        if (isEditing && isLibCode) {
            // We're editing a code library code, so update all other components in the model that are using that code
            // Components

            const accessorMatches = getMatchCodeRefs(components, setCodeRef, editingCodeRef);
            if (!isEmpty(accessorMatches)) {
                accessorMatches.forEach((path) => {
                    if (!path.includes(fieldAccessor)) {
                        batch.queue(path, fieldValue);
                        batch.queue(`${path}_effRVal`, effRVal || nomRVal);
                        batch.queue(`${path}_nomRVal`, nomRVal);

                        if (warningType === "compression") {
                            batch.queue(`${path}_warning`, "compression");
                        } else {
                            batch.queue(`${path}_warning`, "");
                        }
                    }
                });
            }

            // We should also update upgrades using this lib code
            // Upgrades (separate action to different reducer - can't batch)
            const fieldName = fieldCodeTypeMap[componentCodeType];
            dispatch(
                updateUpgradeCodes({
                    oldCodeRef: editingCodeRef,
                    newCodeRef,
                    values: {
                        [`${fieldName}`]: fieldValue,
                        [`${fieldName}_nomRVal`]: nomRVal,
                        [`${fieldName}_effRVal`]: effRVal || nomRVal,
                    },
                    fieldName,
                })
            );

            if (componentCodeType === "CrawlspaceWall") {
                //We must check for pony wall matches
                const fieldName = "ponyWallInsType";
                dispatch(
                    updateUpgradeCodes({
                        oldCodeRef: editingCodeRef,
                        newCodeRef,
                        values: {
                            [`${fieldName}`]: fieldValue,
                            [`${fieldName}_nomRVal`]: nomRVal,
                            [`${fieldName}_effRVal`]: effRVal || nomRVal,
                        },
                        fieldName,
                    })
                );
            }
        }

        // ***************************************
        // 3.4. CONDITIONAL - Delete pre-edited library code from model codes
        // April 2024 update: This was causing problems, and there are too many risks associated with deleting codes in this way
        // ***************************************
        if (forceNewCodeRef && addToLibrary) {
            // const otherComponentsWithCode =
            //     getMatchCodeRefs(components, setCodeRef, editingCodeRef).filter(
            //         (path) => !path.includes(fieldAccessor)
            //     ) || [];
            // //If isLibCode == undefined AND there are other components in the model using that code, do NOT remove the code from the model.
            // // if (!(!isEmpty(otherComponentsWithCode) && isLibCode == null)) {
            // if (isEmpty(otherComponentsWithCode) || isLibCode) {
            //     // console.log("editingCodeRef was removed from the model", otherComponentsWithCode, isLibCode);
            //     batch.queue("model.modelData.codes", {
            //         ...Object.keys(modelCodes).reduce((acc, curr) => {
            //             return curr === editingCodeRef ? acc : { ...acc, [curr]: modelCodes[curr] };
            //         }, {}),
            //         [setCodeRef]: code,
            //     });
            // } else {
            //     // console.log("not deleting editingCodeRef", isEmpty(otherComponentsWithCode), isLibCode);
            // }
            // batch.queue("model.modelData.codes", {
            //     ...Object.keys(modelCodes).reduce((acc, curr) => {
            //         return curr === editingCodeRef ? acc : { ...acc, [curr]: modelCodes[curr] };
            //     }, {}),
            //     [setCodeRef]: code,
            // });
        }

        // Commit all batched changes in a single Redux update
        batch.commit();
    }

    // ***************************************
    // 4. If in code library or "add to code library" is selected, do code library things
    // ***************************************
    if (addToLibrary || isCodeLibrary) {
        await dispatch(
            addToCodeLib(
                uid,
                {
                    ...rest,
                    nominalRValue: nomRVal,
                    effectiveRValue: effRVal || nomRVal,
                    label,
                    codeType: codeTypeLabel,
                    componentType: componentCodeType,
                    value,
                    warningType,
                    codeRef: setCodeRef,
                },
                componentCodeType,
                setCodeRef //Overwrite codeRef if needed here
            )
        )
            .then(() => {
                //If we're overwriting an existing code, delete the old one from the library
                if (forceNewCodeRef) {
                    dispatch(deleteCode(uid, editingCodeRef, componentCodeType));
                }
            })
            .catch(() => {});
    }
};

export const windowCodeLogic = async ({
    change,
    componentCodeType = "Window",
    componentId,
    code: initialCode,
    codeType, // "S" or "U"
    isCodeLibrary = false,
    isEditing = false,
    editingCodeRef = "",
    dispatch,
    getWindowRValue,
    fieldAccessor,
    components,
    modelCodes,
    addToLibrary = false,
    addToCodeLib,
    uid,
    deleteCode,
    updateUpgradeCodes,
    setAsModelDefault = false,
    selectedComponents = [],
    currentPackage,
    selectedUpgrade,
    isEcmInputMap = false,
    ecmInputMapUpgradeType = "",
    ecmInputMapUpgradeId = "",
    ecmInputMapUpgrade: initialEcmInputMapUpgrade = {},
    ecmInputMapLocation = "",
}) => {
    const code = removeUndefined(initialCode);
    const ecmInputMapUpgrade = removeUndefined(initialEcmInputMapUpgrade);

    const { codeRef, value, nominalRValue = null, label, isLibCode, ...rest } = code;
    const codeTypeLabel = codeType === "U" ? "UserDefined" : "Standard";

    //selectedComponents will look like: "wall.wall0.subcomponents.window.window0005"
    //fieldAccessor will look like: "model.modelData.components.wall.wall0.subcomponents.window.window0005"

    // ***************************************
    // 1. Create and change code ref
    // If we are updating a library code (either isLibCode===TRUE && !isCodeLibrary or isEditing===TRUE && isCodeLibrary===TRUE)
    // Then we must create a new codeRef
    // ***************************************
    const newCodeRef = `${componentCodeType}-${codeType}-${moment().format("YYYYMMDDHHmmssSS")}`;
    const forceNewCodeRef = (isLibCode && !isCodeLibrary) || (isEditing && isCodeLibrary) || isEditing;
    const setCodeRef = forceNewCodeRef ? newCodeRef : editingCodeRef || newCodeRef;

    // ***************************************
    // 2. Fetch rValue
    // ***************************************
    let rVal = 0;
    let windowEr = 0;
    let windowShgc = 0;
    const rValueComponent = isCodeLibrary ? "codeLib" : componentId;
    const rValueCalcfieldId = isCodeLibrary ? editingCodeRef || newCodeRef : "windowInsType";

    //Fetch rval, er, shgc
    await dispatch(
        getWindowRValue({
            codeString: value,
            codeType: componentCodeType,
            componentId: rValueComponent,
            fieldId: rValueCalcfieldId,
        })
    ).then(({ rVal: newRVal, windowEr: newWindowEr, windowShgc: newWindowShgc }) => {
        rVal = newRVal;
        windowEr = newWindowEr;
        windowShgc = newWindowShgc;
    });

    // ***************************************
    // 3. If in model, do model things
    // ***************************************
    if (!isCodeLibrary) {
        const batch = createChangeBatch(dispatch);

        const code = {
            ...rest,
            nominalRValue: rVal,
            label,
            value,
            codeRef: setCodeRef,
            er: windowEr,
            shgc: windowShgc,
        };

        //Add to upgrade codes if in upgrade library (Redux + upgrade drawer form `codes`)
        if (currentPackage && selectedUpgrade) {
            syncUpgradeLibraryCodesToDrawerForm(dispatch, batch.queue, {
                currentPackage,
                selectedUpgrade,
                codeRef: code.codeRef,
                codePayload: code,
            });
        }

        if (isEcmInputMap) {
            handleEcmInputMapCodeLogic({
                queueChange: batch.queue,
                code,
                dispatch,
                ecmInputMapUpgradeType,
                ecmInputMapUpgradeId,
                ecmInputMapUpgrade,
                ecmInputMapLocation,
            });
        }

        batch.queue(`model.modelData.codes.${setCodeRef}`, code);

        const fieldValue = {
            codeLabel: label,
            codeRef: setCodeRef,
        };

        // Update model "window type" field in window
        batch.queue(`${fieldAccessor}.windowType`, fieldValue);
        batch.queue(`${fieldAccessor}.windowType_nomRVal`, rVal);
        batch.queue(`${fieldAccessor}.windowType_effRVal`, rVal);
        batch.queue(`${fieldAccessor}.er`, windowEr);
        batch.queue(`${fieldAccessor}.shgc`, windowShgc);

        if (setAsModelDefault) {
            const defaultValue = {
                windowType: fieldValue,
                er: windowEr,
                shgc: windowShgc,
                windowType_nomRVal: rVal,
                windowType_effRVal: rVal,
            };
            batch.queue("model.modelData.defaultCodes.Window", defaultValue);
        }

        if (!isEmpty(selectedComponents)) {
            selectedComponents.forEach((path) => {
                batch.queue(`model.modelData.components.${path}.windowType`, fieldValue);
                batch.queue(`model.modelData.components.${path}.windowType_effRVal`, rVal);
                batch.queue(`model.modelData.components.${path}.windowType_nomRVal`, rVal);
                batch.queue(`model.modelData.components.${path}.er`, windowEr);
                batch.queue(`model.modelData.components.${path}.shgc`, windowShgc);
            });
        }

        // Nov 22, 2022 --> Changing this logic to align with how H2k handles editing codes (see standard codes logic)
        if (isEditing && isLibCode) {
            //Updating all codes in the model with the same codeRef if we're editing a code library code
            const accessorsToUpdate = getMatchingWindowCodeRefs(
                components,
                editingCodeRef,
                fieldAccessor.split("model.modelData.components.")[1]
            );
            if (!isEmpty(accessorsToUpdate)) {
                accessorsToUpdate.forEach((path) => {
                    batch.queue(`${path}.windowType`, fieldValue);
                    batch.queue(`${path}.windowType_effRVal`, rVal);
                    batch.queue(`${path}.windowType_nomRVal`, rVal);
                    batch.queue(`${path}.er`, windowEr);
                    batch.queue(`${path}.shgc`, windowShgc);
                });
            }

            // Upgrades
            const fieldName = fieldCodeTypeMap[componentCodeType];
            dispatch(
                updateUpgradeCodes({
                    oldCodeRef: editingCodeRef,
                    newCodeRef,
                    values: {
                        [`${fieldName}`]: fieldValue,
                        [`${fieldName}_nomRVal`]: rVal,
                        [`${fieldName}_effRVal`]: rVal,
                        er: windowEr,
                        shgc: windowShgc,
                    },
                    fieldName,
                })
            );
        }

        batch.commit();

        if (forceNewCodeRef && addToLibrary) {
            //Must delete from modelCodes here at the end, after updating every other codeRef
            // const otherComponentsWithCode =
            //     getMatchingWindowCodeRefs(
            //         components,
            //         editingCodeRef,
            //         fieldAccessor.split("model.modelData.components.")[1]
            //     ).filter((path) => !path.includes(fieldAccessor.split("model.modelData.components.")[1])) || [];
            // if (isEmpty(otherComponentsWithCode) || isLibCode) {
            //     change("model.modelData.codes", {
            //         ...Object.keys(modelCodes).reduce((acc, curr) => {
            //             return curr === editingCodeRef ? acc : { ...acc, [curr]: modelCodes[curr] };
            //         }, {}),
            //         [setCodeRef]: code,
            //     });
            // }
            // change("model.modelData.codes", {
            //     ...Object.keys(modelCodes).reduce((acc, curr) => {
            //         return curr === editingCodeRef ? acc : { ...acc, [curr]: modelCodes[curr] };
            //     }, {}),
            //     [setCodeRef]: code,
            // });
        }

        // ***************************************
        // 4. If in code library or "add to code library" is selected, do code library things
        // ***************************************
        if (addToLibrary || isCodeLibrary) {
            await dispatch(
                addToCodeLib(
                    uid,
                    {
                        ...rest,
                        nominalRValue: rVal,
                        label,
                        description: label,
                        codeType: codeTypeLabel,
                        componentType: componentCodeType,
                        value,
                        er: windowEr,
                        shgc: windowShgc,
                        codeRef: setCodeRef,
                    },
                    componentCodeType,
                    setCodeRef
                )
            )
                .then(() => {
                    //If we're overwriting an existing code, delete the old one
                    if (forceNewCodeRef) {
                        dispatch(deleteCode(uid, editingCodeRef, componentCodeType));
                    }
                })
                .catch(() => {});
        }
    }

    // ***************************************
    // 4. If in code library or "add to code library" is selected, do code library things
    // ***************************************
    if (addToLibrary || isCodeLibrary) {
        await dispatch(
            addToCodeLib(
                uid,
                {
                    ...rest,
                    nominalRValue: rVal,
                    rVal,
                    windowEr,
                    windowShgc,
                    label,
                    codeType: codeTypeLabel,
                    componentType: componentCodeType,
                    value,
                    codeRef: setCodeRef,
                },
                componentCodeType,
                setCodeRef //Overwrite codeRef if needed here
            )
        )
            .then(() => {
                //If we're overwriting an existing code, delete the old one from the library
                if (forceNewCodeRef) {
                    dispatch(deleteCode(uid, editingCodeRef, componentCodeType));
                }
            })
            .catch(() => {});
    }
};

export const lintelCodeLogic = async ({
    change,
    componentCodeType,
    code: initialCode,
    codeType, // "S" or "U"
    isCodeLibrary = false,
    isEditing = false,
    editingCodeRef = "",
    dispatch,
    fieldAccessor,
    components,
    modelCodes,
    addToLibrary = false,
    addToCodeLib,
    uid,
    deleteCode,
    updateUpgradeCodes,
    currentPackage,
    selectedUpgrade,
    isEcmInputMap = false,
    ecmInputMapUpgradeType = "",
    ecmInputMapUpgradeId = "",
    ecmInputMapUpgrade: initialEcmInputMapUpgrade = {},
    ecmInputMapLocation = "",
}) => {
    const code = removeUndefined(initialCode);
    const ecmInputMapUpgrade = removeUndefined(initialEcmInputMapUpgrade);

    const { codeRef, value, nominalRValue = null, label, isLibCode, ...rest } = code;
    const codeTypeLabel = codeType === "U" ? "UserDefined" : "Standard";

    // ***************************************
    // 1. Create and change code ref
    // If we are updating a library code (either isLibCode===TRUE && !isCodeLibrary or isEditing===TRUE && isCodeLibrary===TRUE)
    // Then we must create a new codeRef
    // ***************************************
    const newCodeRef = `${componentCodeType}-${codeType}-${moment().format("YYYYMMDDHHmmssSS")}`;
    const forceNewCodeRef = (isLibCode && !isCodeLibrary) || (isEditing && isCodeLibrary) || isEditing;
    const setCodeRef = forceNewCodeRef ? newCodeRef : editingCodeRef || newCodeRef;

    // ***************************************
    // 2. If in model, do model things
    // ***************************************
    if (!isCodeLibrary) {
        const code = { ...rest, nominalRValue: 0, label, value, codeRef: setCodeRef };

        //Add to upgrade codes if in upgrade library (Redux + upgrade drawer form `codes`)
        if (currentPackage && selectedUpgrade) {
            syncUpgradeLibraryCodesToDrawerForm(dispatch, change, {
                currentPackage,
                selectedUpgrade,
                codeRef: code.codeRef,
                codePayload: code,
            });
        }

        if (isEcmInputMap) {
            handleEcmInputMapCodeLogic({
                change,
                code,
                dispatch,
                ecmInputMapUpgradeType,
                ecmInputMapUpgradeId,
                ecmInputMapUpgrade,
                ecmInputMapLocation,
            });
        }

        change(`model.modelData.codes.${setCodeRef}`, code);

        const fieldValue = {
            codeLabel: label,
            codeRef: setCodeRef,
        };

        // Update model "lintel type" field in wall
        change(fieldAccessor, fieldValue);

        //If it is a code library code we must update all other Walls with the same reference
        if (isEditing && isLibCode) {
            const accessorMatches = getMatchCodeRefs(components, setCodeRef, editingCodeRef);
            if (!isEmpty(accessorMatches)) {
                accessorMatches.forEach((path) => {
                    if (!path.includes(fieldAccessor)) {
                        change(path, fieldValue);
                    }
                });
            }

            // Upgrades (LINTELS)
            const fieldName = fieldCodeTypeMap[componentCodeType];
            dispatch(
                updateUpgradeCodes({
                    oldCodeRef: editingCodeRef,
                    newCodeRef,
                    values: {
                        [`${fieldName}`]: fieldValue,
                    },
                    fieldName,
                })
            );
        }

        if (forceNewCodeRef && addToLibrary) {
            //Must delete from modelCodes here at the end, after updating every other codeRef
            // const otherComponentsWithCode =
            //     getMatchCodeRefs(components, setCodeRef, editingCodeRef).filter(
            //         (path) => !path.includes(fieldAccessor)
            //     ) || [];
            // if (isEmpty(otherComponentsWithCode) || isLibCode) {
            //     change("model.modelData.codes", {
            //         ...Object.keys(modelCodes).reduce((acc, curr) => {
            //             return curr === editingCodeRef ? acc : { ...acc, [curr]: modelCodes[curr] };
            //         }, {}),
            //         [setCodeRef]: code,
            //     });
            // }
            // change("model.modelData.codes", {
            //     ...Object.keys(modelCodes).reduce((acc, curr) => {
            //         return curr === editingCodeRef ? acc : { ...acc, [curr]: modelCodes[curr] };
            //     }, {}),
            //     [setCodeRef]: code,
            // });
        }
    }

    if (addToLibrary || isCodeLibrary) {
        await dispatch(
            addToCodeLib(
                uid,
                {
                    ...rest,
                    label,
                    nominalRValue: 0,
                    codeType: codeTypeLabel,
                    componentType: componentCodeType,
                    value,
                    codeRef: setCodeRef,
                },
                componentCodeType,
                setCodeRef
            )
        )
            .then(() => {
                //If we're overwriting an existing code, delete the old one
                if (forceNewCodeRef) {
                    dispatch(deleteCode(uid, editingCodeRef, componentCodeType));
                }
            })
            .catch(() => {});
    }
};

export const codeSort = (a, b) => {
    if (a.label < b.label) {
        return -1;
    }
    if (a.label > b.label) {
        return 1;
    }
    return 0;
};

export const layerSort = (a, b) => {
    if (a.layer < b.layer) {
        return -1;
    }
    if (a.layer > b.layer) {
        return 1;
    }
    return 0;
};

export const agCodeCompMap = {
    Wall: "wall",
    FloorHeader: "floorHeader",
    Floor: "expFloor",
    Ceiling: "ceiling",
    CeilingFlat: "ceiling-flat",
};

export const agCodeCompLabelMap = {
    Wall: "wall",
    FloorHeader: "floor header",
    Floor: "exposed floor",
    Ceiling: "ceiling",
    CeilingFlat: "flat ceiling",
};

export const bgCodeCompMap = {
    BasementWall: "basement",
    CrawlspaceWall: "crawlspace",
    Lintel: "wall",
    FloorsAbove: "basement",
    FloorsAdded: "slab",
};

export const codeCompMap = {
    ...agCodeCompMap,
    ...bgCodeCompMap,
};

export const standardLayerLogic = async ({
    change,
    uDefCode: initialUDefCode = {},
    componentType,
    isCodeLibrary = false,
    isEditing = false,
    editingCodeRef = "",
    dispatch,
    getUDefRValue = () => {},
    fieldAccessor,
    components,
    modelCodes,
    addToLibrary = false,
    addToCodeLib,
    uid,
    deleteCode,
    setAsModelDefault = false,
    selectedComponents = [],
    currentPackage = null,
    selectedUpgrade,
    componentId,
    isEcmInputMap = false,
    ecmInputMapUpgradeType = "",
    ecmInputMapUpgradeId = "",
    ecmInputMapUpgrade: initialEcmInputMapUpgrade = {},
    ecmInputMapLocation = "",
}) => {
    const uDefCode = removeUndefined(initialUDefCode);
    const ecmInputMapUpgrade = removeUndefined(initialEcmInputMapUpgrade);

    const { label = "", isLibCode } = uDefCode;

    // 1. Create and change code ref

    componentType = componentType === "PonyWall" ? "CrawlspaceWall" : componentType;

    const newCodeRef = `${componentType}-U-${idIfyDate()}`;
    const forceNewCodeRef = (isLibCode && !isCodeLibrary) || (isEditing && isCodeLibrary) || isEditing;
    const setCodeRef = forceNewCodeRef ? newCodeRef : editingCodeRef || newCodeRef;

    // ***************************************
    // 2. Fetch rValue
    // ***************************************
    const nomRVal = getNominalRValueUDef(uDefCode);
    let effRVal = 0.1;

    const inputName = fieldCodeTypeMap[componentType];
    const rValueComponent = isCodeLibrary ? "codeLib" : componentId;
    const rValueCalcfieldId = isCodeLibrary ? setCodeRef : inputName;

    if (!["Lintel", "FloorsAdded"].includes(componentType)) {
        await dispatch(
            getUDefRValue({
                codeObj: uDefCode,
                codeType: componentType,
                componentId: rValueComponent,
                fieldId: rValueCalcfieldId,
            })
        ).then(({ effRVal: newEffRVal = 0.1 }) => {
            effRVal = newEffRVal;
        });
    }

    // ***************************************
    // 3. If in model, do model things
    // ***************************************
    if (!isCodeLibrary) {
        const batch = createChangeBatch(dispatch);

        const code = {
            ...uDefCode,
            codeRef: setCodeRef,
            nominalRValue: nomRVal,
            effectiveRValue: effRVal,
        };

        //Add to upgrade codes if in upgrade library (Redux + upgrade drawer form `codes`)
        if (currentPackage && selectedUpgrade) {
            syncUpgradeLibraryCodesToDrawerForm(dispatch, batch.queue, {
                currentPackage,
                selectedUpgrade,
                codeRef: code.codeRef,
                codePayload: code,
            });
        }

        if (isEcmInputMap) {
            handleEcmInputMapCodeLogic({
                queueChange: batch.queue,
                code,
                dispatch,
                ecmInputMapUpgradeType,
                ecmInputMapUpgradeId,
                ecmInputMapUpgrade,
                ecmInputMapLocation,
            });
        }

        batch.queue(`model.modelData.codes.${setCodeRef}`, code);

        const fieldValue = {
            codeLabel: label,
            codeRef: setCodeRef,
        };

        // Update model "type" field in component
        batch.queue(fieldAccessor, fieldValue);
        batch.queue(`${fieldAccessor}_effRVal`, effRVal || nomRVal);
        batch.queue(`${fieldAccessor}_nomRVal`, nomRVal);

        const compField = fieldPathCodeTypeMap[componentType];
        if (setAsModelDefault) {
            let defaultValue = {};

            if (compField.includes(".")) {
                let first = compField.split(".")[0];
                let second = compField.split(".")[1];
                defaultValue = {
                    [first]: {
                        [second]: fieldValue,
                        [`${second}_nomRVal`]: nomRVal,
                        [`${second}_effRVal`]: effRVal || nomRVal,
                    },
                };
            } else {
                defaultValue = {
                    [compField]: fieldValue,
                    [`${compField}_nomRVal`]: nomRVal,
                    [`${compField}_effRVal`]: effRVal || nomRVal,
                };
            }
            batch.queue(`model.modelData.defaultCodes.${componentType}`, defaultValue);
        }

        if (!isEmpty(selectedComponents)) {
            selectedComponents.forEach((path) => {
                batch.queue(`model.modelData.components.${path}.${compField}`, fieldValue);
                batch.queue(`model.modelData.components.${path}.${compField}_effRVal`, effRVal || nomRVal);
                batch.queue(`model.modelData.components.${path}.${compField}_nomRVal`, nomRVal);
            });
        }

        //If it is a code library code we must update all other Walls with the same reference
        if (isEditing && isLibCode) {
            const accessorMatches =
                componentType === "FloorHeader"
                    ? getMatchFloorHeaderCodeRefs(components, editingCodeRef)
                    : getMatchCodeRefs(components, setCodeRef, editingCodeRef);

            if (!isEmpty(accessorMatches)) {
                // Check path
                accessorMatches.forEach((path) => {
                    if (!path.includes(fieldAccessor)) {
                        batch.queue(path, fieldValue);
                        batch.queue(`${path}_effRVal`, effRVal || nomRVal);
                        batch.queue(`${path}_nomRVal`, nomRVal);
                    }
                });
            }
        }

        batch.commit();

        if (forceNewCodeRef && addToLibrary) {
            //Must delete from modelCodes here at the end, after updating every other codeRef
            // const otherComponentsWithCode =
            //     getMatchCodeRefs(components, setCodeRef, editingCodeRef).filter(
            //         (path) => !path.includes(fieldAccessor)
            //     ) || [];
            // if (isEmpty(otherComponentsWithCode) || isLibCode) {
            //     change(`model.modelData.codes`, {
            //         ...Object.keys(modelCodes).reduce((acc, curr) => {
            //             return curr === editingCodeRef ? acc : { ...acc, [curr]: modelCodes[curr] };
            //         }, {}),
            //         [setCodeRef]: code,
            //     });
            // }
            // change(`model.modelData.codes`, {
            //     ...Object.keys(modelCodes).reduce((acc, curr) => {
            //         return curr === editingCodeRef ? acc : { ...acc, [curr]: modelCodes[curr] };
            //     }, {}),
            //     [setCodeRef]: code,
            // });
        }
    }

    // ***************************************
    // 4. If in code library or "add to code library" is selected, do code library things
    // ***************************************
    if (addToLibrary || isCodeLibrary) {
        await dispatch(
            addToCodeLib(
                uid,
                {
                    ...uDefCode,
                    nominalRValue: nomRVal,
                    effectiveRValue: effRVal || nomRVal,
                    label,
                    codeType: "User Defined",
                    componentType: componentType,
                    codeRef: setCodeRef,
                },
                componentType,
                setCodeRef
            )
        )
            .then(() => {
                //If we're overwriting an existing code, delete the old one
                if (forceNewCodeRef) {
                    dispatch(deleteCode(uid, editingCodeRef, componentType));
                }
            })
            .catch(() => {});
    }
};

export const windowLayerLogic = async ({
    change,
    uDefCode: initialUDefCode = {},
    componentType,
    isCodeLibrary = false,
    isEditing = false,
    editingCodeRef = "",
    dispatch,
    windowAccessor,
    components,
    modelCodes,
    addToLibrary = false,
    addToCodeLib,
    uid,
    deleteCode,
    setAsModelDefault = false,
    selectedComponents = [],
    currentPackage = null,
    selectedUpgrade,
    isEcmInputMap = false,
    ecmInputMapUpgradeType = "",
    ecmInputMapUpgradeId = "",
    ecmInputMapUpgrade: initialEcmInputMapUpgrade = {},
    ecmInputMapLocation = "",
}) => {
    const uDefCode = removeUndefined(initialUDefCode);
    const ecmInputMapUpgrade = removeUndefined(initialEcmInputMapUpgrade);
    
    const {
        label = "",
        isLibCode,
        layers: { window = {}, windowLegacy = {} },
    } = uDefCode;
    const { shgc = 0, rValue = 0 } = Object.values({ ...window, ...windowLegacy }).reduce(
        (acc, { shgc = 0, ...cur }) => {
            if (!isEmpty(cur)) {
                const curKeys = Object.keys(cur);
                var rValue = 0;

                // Window Overall
                if (curKeys.includes("overallThermalResistance")) {
                    const { id, value } = cur.overallThermalResistance;

                    if (id === 0) {
                        rValue = value;
                    } else if (id === 1) {
                        rValue = value > 0 ? 1 / value : 0;
                    }
                }
                // Window Legacy
                else if (curKeys.includes("rsiValues")) {
                    rValue = cur.rsiValues.centreOfGlass;
                }

                return {
                    ...acc,
                    shgc,
                    rValue,
                };
            }
            return {};
        },
        {}
    );

    // 1. Create and change code ref
    const newCodeRef = `Window-U-${idIfyDate()}`;
    const forceNewCodeRef = (isLibCode && !isCodeLibrary) || (isEditing && isCodeLibrary) || isEditing;
    const setCodeRef = forceNewCodeRef ? newCodeRef : editingCodeRef || newCodeRef;

    // TODO: 2. Fetch rValue

    // ***************************************
    // 3. If in model, do model things
    // ***************************************
    if (!isCodeLibrary) {
        const code = {
            ...uDefCode,
            codeRef: setCodeRef,
            nominalRValue: rValue,
            rValue: rValue,
            er: 0,
            shgc: shgc,
        };

        //Add to upgrade codes if in upgrade library (Redux + upgrade drawer form `codes`)
        if (currentPackage && selectedUpgrade) {
            syncUpgradeLibraryCodesToDrawerForm(dispatch, change, {
                currentPackage,
                selectedUpgrade,
                codeRef: code.codeRef,
                codePayload: code,
            });
        }

        if (isEcmInputMap) {
            handleEcmInputMapCodeLogic({
                change,
                code,
                dispatch,
                ecmInputMapUpgradeType,
                ecmInputMapUpgradeId,
                ecmInputMapUpgrade,
                ecmInputMapLocation,
            });
        }

        change(`model.modelData.codes.${setCodeRef}`, code);

        const fieldValue = {
            codeLabel: label,
            codeRef: setCodeRef,
        };

        // Update model "window type" field in window
        change(`${windowAccessor}.windowType`, fieldValue);
        change(`${windowAccessor}.windowType_nomRVal`, rValue);
        change(`${windowAccessor}.windowType_effRVal`, rValue);
        change(`${windowAccessor}.shgc`, shgc);
        change(`${windowAccessor}.er`, 0);

        if (setAsModelDefault) {
            const defaultValue = {
                windowType: fieldValue,
                er: 0,
                shgc: shgc,
                windowType_nomRVal: rValue,
                windowType_effRVal: rValue,
            };
            change(`model.modelData.defaultCodes.Window`, defaultValue);
        }

        if (!isEmpty(selectedComponents)) {
            selectedComponents.forEach((path) => {
                change(`model.modelData.components.${path}.windowType`, fieldValue);
                change(`model.modelData.components.${path}.windowType_effRVal`, rValue);
                change(`model.modelData.components.${path}.windowType_nomRVal`, rValue);
                change(`model.modelData.components.${path}.er`, 0);
                change(`model.modelData.components.${path}.shgc`, shgc);
            });
        }

        if (isEditing && isLibCode) {
            const accessorsToUpdate = getMatchingWindowCodeRefs(
                components,
                editingCodeRef,
                windowAccessor.split("model.modelData.components.")[1]
            );
            if (!isEmpty(accessorsToUpdate)) {
                accessorsToUpdate.forEach((path) => {
                    change(`${path}.windowType`, fieldValue);
                    change(`${path}.windowType_effRVal`, rValue);
                    change(`${path}.windowType_nomRVal`, rValue);
                    change(`${path}.er`, 0);
                    change(`${path}.shgc`, shgc);
                });
            }
        }

        if (forceNewCodeRef && addToLibrary) {
            //Must delete from modelCodes here at the end, after updating every other codeRef
            // const otherComponentsWithCode =
            //     getMatchingWindowCodeRefs(
            //         components,
            //         editingCodeRef,
            //         windowAccessor.split("model.modelData.components.")[1]
            //     ).filter((path) => !path.includes(windowAccessor.split("model.modelData.components.")[1])) || [];
            // if (isEmpty(otherComponentsWithCode) || isLibCode) {
            //     change(`model.modelData.codes`, {
            //         ...Object.keys(modelCodes).reduce((acc, curr) => {
            //             return curr === editingCodeRef ? acc : { ...acc, [curr]: modelCodes[curr] };
            //         }, {}),
            //         [setCodeRef]: code,
            //     });
            // }
            // change(`model.modelData.codes`, {
            //     ...Object.keys(modelCodes).reduce((acc, curr) => {
            //         return curr === editingCodeRef ? acc : { ...acc, [curr]: modelCodes[curr] };
            //     }, {}),
            //     [setCodeRef]: code,
            // });
        }
    }

    // ***************************************
    // 4. If in code library or "add to code library" is selected, do code library things
    // ***************************************
    if (addToLibrary || isCodeLibrary) {
        await dispatch(
            addToCodeLib(
                uid,
                {
                    ...uDefCode,
                    nominalRValue: rValue,
                    er: 0,
                    shgc: shgc,
                    label,
                    codeType: "User Defined",
                    componentType: componentType,
                    codeRef: setCodeRef,
                },
                componentType,
                setCodeRef
            )
        )
            .then(() => {
                //If we're overwriting an existing code, delete the old one
                if (forceNewCodeRef) {
                    dispatch(deleteCode(uid, editingCodeRef, "Window"));
                }
            })
            .catch(() => {});
    }
};

export const extractCodesFromCodeLib = (codeLib = {}) => {
    return Object.entries(codeLib)
        .map(([key, value]) => Object.keys(value.codes).map((k) => ({ [k]: codeLib[key].codes[k] })))
        .flat()
        .reduce((acc, code) => {
            return { ...acc, ...code };
        }, {});
};

export const extractPrebuiltEcmCodes = (upgradeType) => {
    return Object.values(prebuiltEcms[upgradeType] ?? {}).reduce(
        (acc, { label, packageValues: { codes = {} } = {} }) => {
            const newCodes = Object.entries(codes).reduce(
                (acc, [codeRef, code]) => ({ ...acc, [codeRef]: { ...code, label } }),
                {}
            );
            return { ...acc, ...newCodes };
        },
        {}
    );
};

export const windowsDefaultCode = Object.fromEntries(
    Object.entries(getModelTemplates("default-v11.13").codes).filter(([codeRef, code]) => codeRef === "Window-S-100000")
);

// Search modeCodes for each codeRef in each upgrade package and return the missing codeRefs
export const getMissingModelCodes = (upgradePackages = {}, modelCodes = []) => {
    const allUsedUpgradeCodes = Object.values(upgradePackages).reduce((acc, upgradePackage) => {
        const packageCodeRefs = Object.entries(upgradePackage.upgrades || {}).reduce((acc, [upgradeId, upgrade]) => {
            const { fields = {} } = upgrade || {};
            const { wall: wallFields = {}, floor: floorFields = {} } = fields || {};
            const upgradeCodeRefs = [];

            [fields, wallFields, floorFields].forEach((fieldsObject) => {
                const newCodeRefs = Object.values(fieldsObject || {})
                    .filter((val) => !!val?.codeRef && val?.codeRef !== "UserSpecified")
                    .map((val) => [
                        val.codeRef,
                        {
                            codeLabel: val.codeLabel,
                            codeRef: val.codeRef,
                            upgradeType: upgrade.upgradeType,
                            upgradeId,
                        },
                    ]);
                upgradeCodeRefs.push(...newCodeRefs);
            });

            return [...acc, ...upgradeCodeRefs];
        }, []);

        return [...acc, ...packageCodeRefs];
    }, []);

    return allUsedUpgradeCodes.filter(([codeRef, codeData]) => !(codeRef in modelCodes));
};

const handleEcmInputMapCodeLogic = ({
    change,
    queueChange,
    code,
    dispatch,
    ecmInputMapUpgradeType,
    ecmInputMapUpgradeId,
    ecmInputMapUpgrade,
    ecmInputMapLocation,
}) => {
    const { codes = {} } = ecmInputMapUpgrade;

    // Use queueChange if provided (batched), otherwise fall back to change (legacy)
    const updateFn = queueChange || change;
    updateFn("newForm.upgradeDrawer.codes", { ...codes, [code.codeRef]: code });

    if (!isEmpty(ecmInputMapUpgrade)) {
        if (ecmInputMapLocation === "community") {
            dispatch(
                updateCommunityEcmInputMapUpgrade(ecmInputMapUpgradeType, ecmInputMapUpgradeId, {
                    ...ecmInputMapUpgrade,
                    codes: { ...codes, [code.codeRef]: code },
                })
            );
        } else if (ecmInputMapLocation === "parametricAnalysis") {
            dispatch(
                updateEcmInputMapUpgrade(ecmInputMapUpgradeType, ecmInputMapUpgradeId, {
                    ...ecmInputMapUpgrade,
                    codes: { ...codes, [code.codeRef]: code },
                })
            );
        }
    }
};